import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { InputDateTimev2 } from "@itandi/itandi-bb-ui/InputDateTimev2";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import type React from "react";
import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { FormProvider, useFormContext } from "react-hook-form";
import { useVendorUsers } from "~/hooks/vendors/useVendorUsers";
import type { Deal } from "~/models/Deal";
import { isOriginVendor } from "~/models/DealVendorCompany";
import type { VisitScheduleInputVendorParam } from "~/models/DealVisitDate";
import type { VendorSession } from "~/models/VendorSession";
import { InspectionCandidateDatesList } from "../../inspection_candidate_dates/InspectionCandidateDatesList";

type Props = {
	deal: Deal;
	session: VendorSession;
};

type ScheduleFormProps = Props & {
	methods: UseFormReturn<VisitScheduleInputVendorParam>;
};

/**
 * 立会日程・現地調査日程登録フォーム（内部コンポーネント）
 * @for vendor
 */

const InnerScheduleForm: React.FC<Props> = ({ deal, session }) => {
	const { data: vendorUsers } = useVendorUsers().index();
	const { register, setValue, watch } =
		useFormContext<VisitScheduleInputVendorParam>();
	const inspectionDate = watch("inspectionDate");
	const surveyDate = watch("surveyDate");
	const inspectionByVendor = deal.inspectionBy === "vendor";

	const [syncInspectionDate, setSyncInspectionDate] = useState(true);
	return (
		<>
			<InspectionCandidateDatesList deal={deal} />
			<FormGroup variant="vertical">
				{inspectionByVendor ? (
					<FormControl label="立会実施日程">
						<InputDateTimev2
							onChange={(date): void => {
								setValue("inspectionDate", date);
								if (syncInspectionDate) {
									setValue("surveyDate", date);
								}
							}}
							value={inspectionDate}
						/>
					</FormControl>
				) : null}
				<FormControl label="現地調査実施日程">
					{inspectionByVendor ? (
						<InputCheckBox
							checked={syncInspectionDate}
							label="立会実施日程と同じ値で補完"
							onChange={(e): void => {
								setSyncInspectionDate(e.target.checked);
								if (e.target.checked) {
									setValue("surveyDate", inspectionDate);
								}
							}}
						/>
					) : null}
					<InputDateTimev2
						disabled={inspectionByVendor ? syncInspectionDate : undefined}
						marginTop={8}
						onChange={(date): void => {
							setValue("surveyDate", date);
						}}
						value={surveyDate}
					/>
				</FormControl>
				{!session.vendorCompany.usePartner &&
					!isOriginVendor(deal, session.vendorCompany.id) &&
					vendorUsers !== null && (
						<FormControl label="施工会社担当者">
							<InputSelect width="100%" {...register("vendorUserId")}>
								<option value="">選択してください</option>
								{vendorUsers.map((vendorUser) => (
									<option key={vendorUser.id} value={vendorUser.id}>
										{vendorUser.name}
									</option>
								))}
							</InputSelect>
						</FormControl>
					)}
			</FormGroup>
		</>
	);
};

/**
 * 立会日程・現地調査日程登録フォーム
 * @for vendor
 */

export const ScheduleForm: React.FC<ScheduleFormProps> = ({
	methods,
	deal,
	session
}) => (
	<FormProvider {...methods}>
		<InnerScheduleForm deal={deal} session={session} />
	</FormProvider>
);
